import { useEffect, useRef, useState } from "react";
import { configService } from "../services/configService";
import { wootricService } from "../services/wootricService";
import type { WootricConfig, WootricUser } from "../types/wootric";

interface UseWootricOptions {
  user: WootricUser | null;
  enabled?: boolean;
}

interface UseWootricResult {
  isLoading: boolean;
  isInitialized: boolean;
  error: Error | null;
}

export function useWootric(options: UseWootricOptions): UseWootricResult {
  const { user, enabled = true } = options;
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const initRef = useRef(false);

  useEffect(() => {
    if (!enabled || !user?.email || initRef.current) {
      setIsLoading(false);
      return;
    }

    const initWootric = async () => {
      try {
        setIsLoading(true);
        const config: WootricConfig = await configService.getWootricConfig();

        if (!config.wootricDomain) {
          setIsLoading(false);
          return;
        }

        wootricService.start({ config, user });
        initRef.current = true;
        setIsInitialized(true);
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error("Failed to initialize Wootric"),
        );
      } finally {
        setIsLoading(false);
      }
    };

    initWootric();

    return () => {
      if (initRef.current) {
        wootricService.cleanup();
        initRef.current = false;
      }
    };
  }, [user, enabled]);

  return { isLoading, isInitialized, error };
}
